/**
 * MobileSheet — the browse canvas's bottom drawer, as a PURE VIEW of two facts
 * it does not own (obs. 5).
 *
 * This file used to own the sheet's position: a `useState<number | string |
 * null>`, a `currentState` re-derived from it by `===` with a silent `'half'`
 * fallback, and a ref-guarded effect that watched a monotonic
 * `mapInteractionSignal` counter to collapse to peek. All of that is gone.
 * The RAIL is a reduced state the hook holds; OPENNESS is derived from the
 * marker preview (`resultsSheetIsOpen`) — it was briefly a `kind` on the rail's
 * own union, and a second copy of a fact is what let a desktop pin click hide a
 * drawer that was not even mounted. This component reads both, dispatches rail
 * events, and holds two pieces of state of its own, each a fact about an
 * element that lives in this file and nowhere else: how deep the LIST is
 * scrolled, and which rail the drawer's last transform transition ENDED on.
 * The second is published as `data-rail`, and it is the drawer's answer to the
 * one question a reader of its pixels cannot decide — "is it there yet, or is
 * it still on its way?" — read that state's own note for why nothing else can
 * answer it. What the list DOES with a touch is not this file's to decide
 * either: it is a column of the rail table (`RAIL_LIST_BEHAVIOUR`), and this
 * file only renders the class it names.
 */
import { ResultsCount } from '@/shared/components/listings';
import { useTranslations } from '@/shared/i18n/useTranslations';
import { Button } from '@letsgethome/ui/button';
import { cn } from '@letsgethome/ui/lib/utils';
import { Z_LAYERS } from '@letsgethome/ui/z-layers';
import { Map as MapIcon } from 'lucide-react';
import { useCallback, useRef, useState, type ReactNode, type TransitionEvent } from 'react';
import { Drawer } from 'vaul';

import { resultsSheetIsOpen } from './listings-selection';
import type { ListingsSheetProps } from './ListingsSheet';
import {
  RAIL_LIST_BEHAVIOUR,
  SHEET_POSITIONS,
  SNAP_FRACTION,
  type RailListBehaviour,
  type SheetPosition,
} from './results-sheet-state';

/**
 * ⚠ THE HEIGHT IS THE FIRST OF obs. 5's THREE CAUSES, AND THE UNIT IS THE
 * WHOLE OF THE ARGUMENT.
 *
 * `Drawer.Content` was `fixed bottom-0 left-0 right-0` with no height at all,
 * so its height was its CONTENT's height — while vaul positions a bottom
 * drawer by translating it down `containerSize.height × (1 − snap)`, where
 * `containerSize.height` is `window.innerHeight` (`useSnapPoints`'
 * `snapPointsOffset` memo, vaul 1.1.2). That transform assumes the drawer is
 * viewport-tall. A ~250 px empty state at `half` therefore put its top at
 * `852 − 250 + 852 × 0.45 = 985` on an 852 px viewport: measured at
 * `top=1008, height=227`, 156 px below the bottom edge, unreachable.
 *
 * ★ `dvh`, NOT `svh` — and this is a deliberate DEPARTURE from the precedent
 * #6835 set one file over, for a reason that is specific to this element.
 * `pageChromeMainClassName` chose `svh` for the `canvas` kind because `main`
 * is a LAYOUT BOUND: content is measured against it, the SMALL viewport is
 * static, and so "iOS's `100dvh`-vs-URL-bar jitter" (the F3 argument this
 * estate wrote down when it deleted the wizard shell's `h-[100dvh]`) is
 * unrepresentable there. Neither half of that reasoning transfers here:
 *
 *   * this height is not a bound anything is measured against. It is a
 *     MIRROR of one number — `window.innerHeight` — because vaul computes the
 *     offset that positions this element from exactly that number, and
 *     re-computes it on every `resize`. The invariant the fix rests on is
 *     `content height === window.innerHeight`, at which point the visible
 *     strip is exactly `snap × viewport` whatever the content is;
 *   * `svh` is the height with the browser UI SHOWN, so the moment the URL
 *     bar collapses it is SMALLER than `window.innerHeight` by `lvh − svh`
 *     (~60–80 px on iOS). vaul would still translate by the larger number,
 *     and the drawer would sit that far too low — obs. 5 again, at reduced
 *     magnitude, on exactly the devices the observation came from. At
 *     `peek` (0.12 × 852 ≈ 102 px) a 60 px error is most of the grab target.
 *
 * `dvh` is the unit whose definition IS `window.innerHeight`, so CSS and
 * vaul's JS agree at every instant, including mid-collapse — the drawer and
 * its transform move together instead of drifting apart. The jitter F3
 * objected to was content reflowing inside a bound; here nothing is measured
 * against the height, so there is nothing to reflow: the visible fraction is
 * invariant across the whole URL-bar transition.
 *
 * `max-h-dvh` beside it so a content-driven grow cannot re-introduce cause 1
 * from the other direction.
 */
const SHEET_VIEWPORT_HEIGHT = 'h-dvh max-h-dvh';

/** Scroll depth (px) past which the floating "Map" button appears. */
const SCROLL_THRESHOLD = 100;

/** vaul's `snapPoints`, DERIVED from the rails — never a second literal. */
const VAUL_SNAP_POINTS = SHEET_POSITIONS.map((position) => SNAP_FRACTION[position]);

/**
 * The rendered rail roster, PUBLISHED to the DOM.
 *
 * ⚠ This exists for the e2e suite and it is not decoration. A browser test of
 * this drawer has to state "at least one rail's worth of it is on screen",
 * which needs the rail's fraction — and a spec CANNOT import one: the
 * Playwright runner loads specs through Node's type stripping, which refuses a
 * `.ts` file under `node_modules`, so a value import from any workspace package
 * fails at run time (`e2e/helpers/owner-listings.ts` records the same
 * constraint for its own vocabulary). `listings-mobile-sheet.spec.ts` therefore
 * hand-copied `PEEK_FRACTION = 0.12` with nothing holding it equal to
 * `SNAP_FRACTION.peek`. It reads this attribute now, so the spec measures the
 * app against the app's OWN numbers and a re-tuned rail cannot leave a stale
 * literal behind.
 */
const SNAP_POINTS_ATTR = VAUL_SNAP_POINTS.join(',');

/**
 * The rail NAMES, in the same order as `data-snap-points`, PUBLISHED to the DOM.
 *
 * The fractions above let a test measure a pixel; these let it name a rail
 * without spelling one. A spec cannot import `SHEET_POSITIONS` for the same
 * reason it cannot import `SNAP_FRACTION` (see above), and a hand-copied
 * `'full'` would be a second spelling of the top rail with nothing holding the
 * two equal — the exact defect `SNAP_POINTS_ATTR` was added to close, one
 * vocabulary over. The LAST entry is the top rail, because `SHEET_POSITIONS`
 * is declared in ascending order and says so.
 */
const RAILS_ATTR = SHEET_POSITIONS.join(',');

/**
 * The top inset the drawer owes the canvas's own fixed top bar at the `full`
 * rail (obs. 2.17).
 *
 * At `full` the content is `h-dvh bottom-0`, i.e. it starts at y=0, while
 * `ListingsTopBar` is `fixed top-0` on `Z_LAYERS.navbar` (z-55) OVER the sheet
 * (z-45) — so the result heading and the first card sat UNDER the bar with no
 * way to scroll them out. At every lower rail the drawer's top edge is already
 * below the bar and an inset would be dead space, which is why it is applied at
 * `full` and nowhere else.
 *
 * `--mobile-header-height` is the SSOT for the mobile top band's height, and it
 * is now what SIZES `ListingsTopBar` as well (it previously had no height at
 * all — a content-derived ~60 px). One variable, two readers, so the inset and
 * the bar cannot drift; the same variable is what `PageShell` subtracts from a
 * fixed-viewport `main` below `md`. Spelled in full as a literal because
 * Tailwind scans source as TEXT: a class composed at runtime mints no rule.
 *
 * ⚠ No `env(safe-area-inset-top)` term. Every `CHROME_SAFE_AREA` member
 * resolves to `0px` today — the viewport meta carries no `viewport-fit=cover`
 * (`SAFE_AREA_INSETS_ARE_INERT` in `z-layers.tsx`) — and the inset belongs to
 * the BAND that occupies the notch, not to the element clearing the band. If
 * the estate ever opts in, `ListingsTopBar` grows the inset and this stays a
 * pure function of the bar's height.
 */
const FULL_RAIL_TOP_INSET = 'pt-[var(--mobile-header-height)]';

/**
 * The list's overflow, per rail BEHAVIOUR — the Tailwind half of
 * `RAIL_LIST_BEHAVIOUR`, which is the SSOT (read the defect there).
 *
 * `overflow-hidden` is not a cosmetic choice and it is not "hide the
 * overflow": it is what stops this element being a SCROLL CONTAINER, and
 * being one is the whole mechanism. A touch's effective `touch-action` is
 * computed only up to the nearest scroll container, so while the list was one
 * at every rail, vaul's `touch-action: none` on the drawer above it never
 * entered the computation and the browser owned every gesture that started
 * inside the list.
 *
 * ⚠ BOTH CLASSES ARE SPELLED OUT, and the `satisfies` is what keeps them
 * total. Tailwind's scanner reads source as TEXT, so a class composed at run
 * time mints no rule at all — the failure `z-layers.tsx`'s `pb-safe` note
 * records. A third behaviour therefore cannot be added without a literal
 * class here for the scanner to find.
 *
 * ★ WHAT THAT OWNERSHIP COSTS AT THE TOP RAIL, measured (2026-09-18). At
 * `full` the list IS a scroll container, so a finger that lands inside it is
 * the browser's: Chromium claims the touch for a native scroll at the FIRST
 * sample that leaves its slop and fires `pointercancel` (measured 4–17 ms
 * after that sample, from an in-page recorder). vaul's `Content` gets the
 * `pointerout` that follows and releases the drag through
 * `handleOnPointerUp(lastKnownPointerEvent)` — so of a pull-down at
 * `scrollTop === 0` vaul ever sees ONE sample, and its verdict on that sample
 * is `useSnapPoints.onRelease`'s (vaul 1.1.2 `index.mjs:623-664`):
 * `velocity = distance / (pointercancel − pointerdown)` against
 * `VELOCITY_THRESHOLD = 0.4` (`:446`), and below the threshold "the closest
 * snap point" to the sample. A 21 px sample therefore collapses the drawer
 * when the main thread answers inside ~52 ms and leaves it on `full` when it
 * does not — which is what a saturated CI executor does (#7192, #7215, and
 * the 2026-09-18T02:15Z group run that failed with #7247's 500 ms mirror
 * PRESENT). The number vaul judges is main-thread latency, not its own
 * `openTime` window, so no clock mirrored from this file could ever have
 * answered it; that mirror is deleted. The e2e case that exercises this path
 * (`listings-mobile-sheet.spec.ts`, "a FLICK down collapses the drawer")
 * sends one sample that already lies closer to the next rail than to `full`,
 * which every release branch then leaves — the argument is in
 * `helpers/touch.ts`'s `touchFlick`. What it does NOT make true: a SLOW
 * pull-down inside the list at the top rail does not collapse the drawer;
 * only a flick does. Making a slow pull a real drag is a change to this
 * element's native scroll ownership at `full`, and is not this file's claim.
 */
const LIST_OVERFLOW = {
  'drags-drawer': 'overflow-hidden',
  scrolls: 'overflow-y-auto',
} as const satisfies Record<RailListBehaviour, string>;

/**
 * The list, for a test to locate.
 *
 * Its overflow is now a FUNCTION of the rail, so neither suite may find it by
 * the class it is asserting — `.overflow-y-auto` names the element at exactly
 * one rail and nothing at the other two — and the e2e half cannot import a
 * constant at all (see `SNAP_POINTS_ATTR`). One testid, read by
 * `MobileSheet.react.test.tsx` and `listings-mobile-sheet.spec.ts`.
 */
const RESULTS_LIST_TESTID = 'results-sheet-list';

/**
 * What the drawer CHROME needs: the state it positions itself from, the count
 * for its header, the infinite-scroll trigger its scroll container fires — and
 * the list itself, as `children`.
 *
 * ★ The list is HANDED to this drawer, never rendered by it (#8722). It used
 * to carry its own copy of the card markup beside the served pane's copy, and
 * the served copy was CSS-hidden below `md` while this one existed only once a
 * client had hydrated — so a phone without JavaScript had neither on screen.
 * `ListingsSheet` now builds ONE `ResultsList` and gives it to exactly one
 * host; this drawer is the enhancement that takes it over on a hydrated phone.
 */
export type MobileSheetProps = Pick<
  ListingsSheetProps,
  'results' | 'onLoadMore' | 'hasMore' | 'sheetPosition' | 'onSheetEvent' | 'markerPreview'
> & {
  /** The ONE `ResultsList` — see `ListingsSheet`. */
  children: ReactNode;
};

export function MobileSheet({
  results,
  onLoadMore,
  hasMore,
  sheetPosition: position,
  onSheetEvent,
  markerPreview,
  children,
}: MobileSheetProps) {
  const t = useTranslations('listings.browse');
  const scrollRef = useRef<HTMLDivElement>(null);
  const [listScrolledPast, setListScrolledPast] = useState(false);

  // The rendered position is READ, not re-derived: a `SheetPosition` is always
  // a rail, so there is no `null` here and no equality chain to fall off the
  // end of. Openness is DERIVED from the preview that is the only reason the
  // drawer ever leaves the screen — see `resultsSheetIsOpen`, and the
  // `markerPreview` prop's note for why this pane takes the fact and not a
  // boolean.
  const isOpen = resultsSheetIsOpen(markerPreview);

  // ── The rail the drawer has COME TO REST on ────────────────────────────────
  //
  // ⚠ THIS IS THE ONE PIECE OF TIMING THE DRAWER DECLARES, AND IT EXISTS
  // BECAUSE NOTHING ELSE CAN OBSERVE IT. vaul moves this element by a 0.5 s
  // `transform` transition, and for the whole of that transition the drawer is
  // BOTH at a rail (the prop below says which) and somewhere else (the pixels
  // say where). A reader of the pixels alone cannot tell the two apart, and one
  // reader that tried — `listings-mobile-sheet.spec.ts`'s "two consecutive
  // bounding boxes agree" poll — was PROVABLY early: vaul's easing is
  // `cubic-bezier(0.32, 0.72, 0, 1)`, whose terminal velocity is ZERO, so a
  // settle predicate with any non-zero threshold necessarily fires before the
  // transition ends, by an amount that is a function of the drag distance and
  // of how fast the poll happens to round-trip. That gap red the suite twice in
  // ten hours (#7192, #7215) — see the spec's own note for the mechanism it
  // opened a window onto.
  //
  // So the drawer STATES it. `arrivedAt` is written by the `transitionend` of
  // the transform that carried the drawer here, and `data-rail` is published
  // only while that rail is still the one the props ask for — i.e. the
  // attribute disappears in the same commit that starts a move and comes back
  // when the move has genuinely finished. It is derived at the render rather
  // than stored twice, so a stale `arrivedAt` is unobservable.
  //
  // ★ WHAT IT DOES NOT CLAIM. A finger DRAG is not a transition — vaul sets
  // `transition: none` and moves the transform imperatively — so between
  // touchdown and release this attribute still names the rail the drawer last
  // came to rest on. That is a true sentence (nothing has come to rest
  // anywhere new) and vaul exposes no signal that would let it be a stronger
  // one; every reader of this attribute observes it after a gesture has ended.
  const [arrivedAt, setArrivedAt] = useState<SheetPosition | null>(null);
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (wasOpen !== isOpen) {
    // A drawer that has left the screen has not ARRIVED anywhere. vaul unmounts
    // the content while `open` is false and animates the next mount in from
    // off-screen, so the rail the last transition ended on is a fact about an
    // element that no longer exists — and re-publishing it would make the
    // drawer declare itself at rest for the whole of its own entrance. React's
    // own "adjust state when a prop changes" spelling: it re-renders before
    // anything is committed, so no reader ever sees the stale pair.
    setWasOpen(isOpen);
    setArrivedAt(null);
  }
  const restingRail = arrivedAt === position ? position : undefined;

  const handleTransitionEnd = useCallback(
    (event: TransitionEvent<HTMLDivElement>) => {
      // A descendant's transition is not the drawer's. `ListingCard`'s surface
      // carries `transition-all`, so every card in the list fires
      // `transitionend` on any property it animates and every one of them
      // BUBBLES through here — and none of them has moved the drawer. Only
      // this element's own transform does, which is what vaul translates.
      if (event.target !== event.currentTarget) return;
      if (event.propertyName !== 'transform') return;
      setArrivedAt(position);
    },
    [position]
  );

  // How deep the LIST is scrolled — a fact about the scroll container and
  // nothing else. It used to fold the rail into itself
  // (`position !== 'peek' && …`), which was already a second reader of the
  // rail and is now an impossible one: the list is a scroll container at
  // exactly one rail (`RAIL_LIST_BEHAVIOUR`), so `onScroll` never fires
  // anywhere else and a stored "scrolled" from the top rail would SURVIVE the
  // drag back down with no event able to clear it. The rail is applied at the
  // render instead, where it is read fresh.
  const handleContentScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;

    setListScrolledPast(container.scrollTop > SCROLL_THRESHOLD);

    // Infinite scroll detection
    if (onLoadMore && hasMore) {
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollHeight - scrollTop - clientHeight < 200) {
        onLoadMore();
      }
    }
  }, [onLoadMore, hasMore]);

  // ⚠ A STATED CONSEQUENCE of the rail table, not an oversight: the list
  // scrolls only at the top rail, so `handleContentScroll` fires only there —
  // and with it BOTH of its readers. The floating "Map" button and the
  // infinite-scroll trigger therefore live at the top rail alone. That is the
  // Rails app's behaviour verbatim (`_updateMapButtonVisibility`: shown at
  // `full`, or once scrolled past the threshold) and it is the standard one: a
  // reader at `half` who wants more drags up first. The crawlable pager anchors
  // (`footer`) are in the served HTML at every rail, so nothing a crawler or a
  // JS-off reader can reach moved.
  const listScrolls = RAIL_LIST_BEHAVIOUR[position] === 'scrolls';

  // The floating "Map" button: scroll the list home, then ASK for peek. The
  // reducer decides what that means — this file no longer sets a snap point.
  const handleMapButtonClick = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: 0, behavior: 'instant' });
    }
    onSheetEvent({ type: 'mapButton' });
  }, [onSheetEvent]);

  return (
    <>
      <Drawer.Root
        // `open` is DERIVED, not a literal and not a stored flag: the one legal
        // way for this drawer to leave the screen is a marker preview taking
        // the bottom edge, so dismissing that preview IS the way back and
        // there is nothing that can be hidden without one on screen.
        open={isOpen}
        modal={false}
        snapPoints={VAUL_SNAP_POINTS}
        activeSnapPoint={SNAP_FRACTION[position]}
        setActiveSnapPoint={(raw) => {
          // ⚠ ONLY WHILE THE DRAWER IS ON SCREEN. vaul reports an active snap
          // point as it closes, and a report from a drawer nobody can see is
          // not a reader's choice of rail — forwarded, it would quietly move
          // the rail the drawer comes back to (`coerceSnap(null)` is the
          // RESTING rail, so a reader at `full` would return to `half`). This
          // is the vaul adapter's own fact about vaul, which is why the guard
          // is here and not in the reducer: the reducer judges events, and an
          // event that should never have been sent is this file's to not send.
          if (isOpen) onSheetEvent({ type: 'vaulSnapped', raw });
        }}
        // Don't dismiss when dragging down past peek
        dismissible={false}
      >
        <Drawer.Portal>
          {/* Accessible title (hidden visually) */}
          <Drawer.Title className="sr-only">{t('propertyListings')}</Drawer.Title>

          <Drawer.Content
            data-testid="results-sheet"
            // The rails this drawer actually rendered with, for the e2e suite
            // to measure against — see `SNAP_POINTS_ATTR`.
            data-snap-points={SNAP_POINTS_ATTR}
            // …and the rail NAMES those fractions belong to, same order.
            data-rails={RAILS_ATTR}
            // The rail the drawer is AT REST on, or absent while it moves —
            // see `restingRail` above. Present only after the transform
            // transition that carried it here has ENDED.
            data-rail={restingRail}
            onTransitionEnd={handleTransitionEnd}
            // The layer is `@letsgethome/ui/z-layers`' claim, not this
            // file's (LET-120). This sheet is persistent page chrome, not a
            // modal surface — `modal={false}`, `dismissible={false}` — so it
            // shares the bottom edge with whatever else a layout pins there.
            // It carried `z-40`, TIED with `MobileBottomNav`, and a tie is
            // resolved by nothing but document order. Nothing renders both
            // here: this group is `chrome: canvas`, and the ordering below is
            // WHY — a tab bar at z-40 under an always-open sheet at z-45
            // would be unreachable, so the browse hubs own their bottom edge
            // and declare no tab bar (F4).
            className={cn(
              'fixed bottom-0 left-0 right-0 flex flex-col bg-background shadow-2xl',
              // Cause 1 of obs. 5 — see `SHEET_VIEWPORT_HEIGHT`'s note for
              // why the unit is `dvh` here and `svh` in `PageShell`.
              SHEET_VIEWPORT_HEIGHT,
              // The inset is a property of the FAMILY, and this member of it
              // carried none: `band()` takes it as a required argument now, so
              // the sheet's own row is where it comes from.
              Z_LAYERS.resultsSheet.safeArea,
              Z_LAYERS.resultsSheet.className,
              // At `full` the drawer's top edge is the viewport's, which is
              // where the canvas's own fixed top bar is — see
              // `FULL_RAIL_TOP_INSET`.
              position === 'full' ? cn('rounded-none', FULL_RAIL_TOP_INSET) : 'rounded-t-2xl'
            )}
          >
            {/* Handle - hidden in full state (matches Rails) */}
            <div
              className={cn(
                'flex flex-col items-center py-3 cursor-grab active:cursor-grabbing touch-none select-none',
                position === 'full' && 'opacity-0 pointer-events-none h-0 py-0'
              )}
            >
              <div className="h-1.5 w-12 rounded-full bg-muted-foreground/30" />
            </div>

            {/* The result count — the SAME component the desktop pane mounts.
                Two hand-written headings is what made this one left-aligned
                while the other was not (obs. 3). */}
            <div className="px-4 pb-3 border-b border-border">
              {(results.kind === 'ready' || results.kind === 'pastTheEnd') && (
                <ResultsCount count={results.totalCount} />
              )}
            </div>

            {/* The list. `flex-1 min-h-0` inside a now viewport-tall column:
                the list, not the drawer, absorbs the content's length, so an
                empty state and a thousand cards leave the drawer exactly the
                same size.

                Whether it SCROLLS is the rail's to say (`RAIL_LIST_BEHAVIOUR`).
                It was `overflow-y-auto` unconditionally, which made it a native
                scroll container at every rail — and a touch's effective
                `touch-action` is computed only up to the nearest scroll
                container, so the browser took every gesture that started in
                here before vaul saw one. */}
            <div
              ref={scrollRef}
              data-testid={RESULTS_LIST_TESTID}
              className={cn(
                'flex-1 min-h-0 overscroll-contain p-4',
                LIST_OVERFLOW[RAIL_LIST_BEHAVIOUR[position]]
              )}
              onScroll={handleContentScroll}
            >
              {/* The ONE list, handed over by `ListingsSheet` — the same
                  `ResultsList` the served pane hosts, carrying the same
                  crawlable pager, so hydration adds no link the served HTML
                  lacks (the link-parity gate's invariant) and no second list. */}
              {children}
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>

      {/* Floating Map Button — shown once the list is scrolled past the
          threshold, which can only happen at the rail whose list SCROLLS. */}
      <Button
        variant="secondary"
        size="lg"
        // Above the sheet it floats over — same seam, same reason as above.
        // Unchanged value: `floatingAction` IS the `z-50` this carried.
        className={cn(
          'fixed left-1/2 -translate-x-1/2 shadow-lg transition-all duration-300',
          // `bottom-6` WITH the device inset folded into the anchor. A `pb-*`
          // would replace the button's own vertical padding (it comes from
          // `size`), which is why this member of CHROME_SAFE_AREA moves the
          // offset instead — see `z-layers`.
          Z_LAYERS.floatingAction.safeArea,
          Z_LAYERS.floatingAction.className,
          'flex items-center gap-2 rounded-full px-6',
          // Hidden while the sheet is, too: it is the sheet's control, and
          // the preview card owns the bottom edge for as long as it is up.
          listScrolls && listScrolledPast && isOpen
            ? 'translate-y-0 opacity-100'
            : 'translate-y-20 opacity-0 pointer-events-none'
        )}
        onClick={handleMapButtonClick}
      >
        <MapIcon className="h-4 w-4" />
        {t('map')}
      </Button>
    </>
  );
}
